import {
  Api,
  ApiResponse,
  ApiResponseFormat,
  emptyProcessResponseResolver,
  paginatedProcessResponseResolver,
  PaginationFilters,
  processApiResponse,
  Sortable,
  sortableToApiFormat,
} from '@moodys/cre-cpm-client-apis.apis.api-base';
import { Language } from '@moodys/cre-cpm-client-apis.apis.services.auth';
import {
  Asset,
  AssetFields,
  AssetFilters,
  CompanyFilter,
  Deal,
  GlobalFilter,
  Loan,
  Portfolio,
  PortfolioFilter,
  Unit,
  UnitFields,
  UnitFilters,
  captainAttributes,
  dealAttributes,
  defaultUnitFields,
  defaultUnitSort,
  loanAttributes,
  FilterBar,
  FiltersBarFilter,
  LoanStressReport,
  LoanStressReportAttributes,
  loanStressReportFields,
  DealStage,
  DealDocument,
  DealsFilters,
  FinancialModel,
  financialModelAttributes,
  FinancialAccount,
  financialAccountAttributes,
  LoansFilters,
  PlatformTenantFilters,
  PlatformTenant,
  tenantAttributes,
  Task,
  taskAttributes,
  LoanSensitivityAnalysis,
  LoanSensitivityAnalysisAttributes,
  loanSensitivityAnalysisFields,
  LoanStressReportDocument,
  LoanStressReportDocumentResponse,
  GlobalFilterFilter,
} from './models';

const dealAssetsFields = [
  'id',
  'source_asset_ref_id',
  'total_area',
  'total_net_area',
  'type_of_use',
  'normalized_address',
  'measured_by',
  'total_net_rent_monthly',
  'currency_exchange_rates',
];

const dealsAssetsFields = [
  'total_area',
  'total_net_area',
  'total_net_rent_monthly',
  'area',
  'rent_per_year',
  'main_type_of_use',
  'measured_by',
  'currency_exchange_rates',
  'asking_price',
  'yield_value',
  'multiplier',
];

class PlatformApi extends Api {
  async getPortfolios(
    fields: string[],
    filters?: PortfolioFilter
  ): Promise<ApiResponse<Portfolio[]>> {
    const params = {
      fields: {
        portfolios: fields.join(','),
      },
      filter: filters,
    };
    const response = await this.request.get<ApiResponseFormat<Portfolio[]>>(
      '/v1/platform/portfolios',
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response);
  }

  async deletePortfolio(
    portfolioId: string,
    companyRefUid: string
  ): Promise<ApiResponse<void>> {
    const body = {
      data: {
        type: 'portfolios',
        id: portfolioId,
        relationships: {
          company: {
            type: 'companies',
            id: companyRefUid,
          },
        },
      },
    };
    const response = await this.request.delete<ApiResponseFormat<void>>(
      `/v1/platform/portfolios/${portfolioId}`,
      {},
      {
        data: body,
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, emptyProcessResponseResolver);
  }

  async getAssets(
    assetFilters: AssetFilters,
    companyFilter: CompanyFilter,
    page?: PaginationFilters,
    sortable?: Sortable,
    fields: AssetFields = {}
  ): Promise<ApiResponse<Asset[]>> {
    const response = await this.request.get<ApiResponseFormat<Asset[]>>(
      '/v1/platform/assets',
      {
        filter: { ...assetFilters, ...companyFilter },
        fields,
        page,
        sort: sortableToApiFormat(sortable),
      },
      { headers: this.getHeaders() }
    );

    return processApiResponse(response, paginatedProcessResponseResolver);
  }

  async getAsset(
    assetId: string,
    companyFilter: CompanyFilter,
    fields: AssetFields = {}
  ): Promise<ApiResponse<Asset>> {
    const response = await this.request.get<ApiResponseFormat<Asset>>(
      `/v1/platform/assets/${assetId}`,
      { fields, filter: companyFilter },
      { headers: this.getHeaders() }
    );

    return processApiResponse(response);
  }

  async createAsset(
    asset: Asset,
    fields: AssetFields = {}
  ): Promise<ApiResponse<Asset>> {
    const response = await this.request.post<ApiResponseFormat<Asset>>(
      '/v1/platform/assets',
      { data: asset, fields },
      { headers: this.getHeaders() }
    );

    return processApiResponse(response);
  }

  async updateAsset(
    asset: Asset,
    fields: AssetFields = {}
  ): Promise<ApiResponse<Asset>> {
    const response = await this.request.put<ApiResponseFormat<Asset>>(
      `/v1/platform/assets/${asset.id}`,
      { data: asset, fields },
      { headers: this.getHeaders() }
    );

    return processApiResponse(response);
  }

  async getUnits(
    unitFilters: UnitFilters,
    page?: PaginationFilters,
    sortable: Sortable = defaultUnitSort,
    fields: UnitFields = {}
  ): Promise<ApiResponse<Unit[]>> {
    const response = await this.request.get<ApiResponseFormat<Unit[]>>(
      '/v1/platform/units',
      {
        filter: unitFilters,
        fields: { ...defaultUnitFields, ...fields },
        page,
        sort: sortableToApiFormat(sortable),
      },
      { headers: this.getHeaders() }
    );

    return processApiResponse(response, paginatedProcessResponseResolver);
  }

  async getUnitsByAsset(
    assetId: string,
    unitFilters: UnitFilters,
    companyFilter: CompanyFilter,
    globalFilters?: GlobalFilter[],
    page?: PaginationFilters,
    sortable: Sortable = defaultUnitSort,
    fields: UnitFields = {}
  ): Promise<ApiResponse<Unit[]>> {
    const response = await this.request.get<ApiResponseFormat<Unit[]>>(
      `/v1/platform/assets/${assetId}/units`,
      {
        filter: { ...unitFilters, ...companyFilter, global: globalFilters },
        fields: { ...defaultUnitFields, ...fields },
        page,
        sort: sortableToApiFormat(sortable),
      },
      { headers: this.getHeaders() }
    );

    return processApiResponse(response, paginatedProcessResponseResolver);
  }

  public async createPortfolio(
    portfolioName: string
  ): Promise<ApiResponse<Portfolio>> {
    const body = {
      fields: {
        portfolios: ['name'].join(','),
      },
      data: {
        type: 'portfolios',
        attributes: {
          name: portfolioName,
        },
      },
    };
    const response = await this.request.post<ApiResponseFormat<Portfolio>>(
      '/v1/platform/portfolios',
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Portfolio>(response);
  }

  public async updateUnit(unit: Unit): Promise<ApiResponse<Unit>> {
    const body = {
      data: unit,
    };

    const response = await this.request.put<ApiResponseFormat<Unit>>(
      `/v1/platform/units/${unit.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Unit>(response);
  }

  public async deleteAsset(asset: Asset): Promise<ApiResponse<void>> {
    const response = await this.request.delete<ApiResponseFormat<void>>(
      `/v1/platform/assets/${asset.id}`,
      {},
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse(response, emptyProcessResponseResolver);
  }

  public async exportAssetsList(
    globalFilters: GlobalFilter[],
    locale: Language
  ): Promise<ApiResponse<void>> {
    const body = {
      filter: {
        global: globalFilters,
      },
    };

    const response = await this.request.post<ApiResponseFormat<void>>(
      '/v1/platform/exports/assets',
      body,
      {
        headers: {
          ...this.getHeaders(),
          'Content-Language': locale,
        },
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async exportRentroll(
    assetId: string,
    locale: Language
  ): Promise<ApiResponse<void>> {
    const response = await this.request.post<ApiResponseFormat<void>>(
      `/v1/platform/exports/assets/${assetId}`,
      {},
      {
        headers: {
          ...this.getHeaders(),
          'Content-Language': locale,
        },
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async getDeals(
    globalFilters: GlobalFilter[],
    filters?: DealsFilters,
    sort?: string,
    page?: PaginationFilters
  ): Promise<ApiResponse<Deal[]>> {
    const fields = {
      deals: dealAttributes.join(','),
      assets: dealsAssetsFields.join(','),
      captain: captainAttributes.join(','),
      deal_stage: 'id',
    };

    const params = {
      fields,
      page: page || {
        size: 99999,
      },
      sort: sort || '-created_at',
      filter: {
        ...(globalFilters ? { ...filters, global: globalFilters } : filters),
      },
    };

    const response = await this.request.get<ApiResponseFormat<Deal[]>>(
      '/v1/platform/deals',
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Deal[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async getDeal(dealId: string): Promise<ApiResponse<Deal>> {
    const params = {
      fields: {
        deals: dealAttributes.join(','),
        assets: dealAssetsFields.join(','),
        captain: captainAttributes.join(','),
        deal_stage: 'id',
      },
    };

    const response = await this.request.get<ApiResponseFormat<Deal>>(
      `/v1/platform/deals/${dealId}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Deal>(response);
  }

  public async createDeal(deal: Deal): Promise<ApiResponse<Deal>> {
    const body = {
      data: deal,
    };

    const response = await this.request.post<ApiResponseFormat<Deal>>(
      '/v1/platform/deals',
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Deal>(response, paginatedProcessResponseResolver);
  }

  public async updateDeal(deal: Deal): Promise<ApiResponse<Deal>> {
    const body = {
      data: deal,
    };
    const response = await this.request.put<ApiResponseFormat<Deal>>(
      `/v1/platform/deals/${deal.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Deal>(response);
  }

  public async exportDeals(
    globalFilters: GlobalFilter[],
    locale: Language,
    like?: string
  ): Promise<ApiResponse<void>> {
    const body = {
      filter: {
        like: like || '',
        global: globalFilters,
      },
    };

    const response = await this.request.post<ApiResponseFormat<void>>(
      '/v1/platform/exports/deals',
      body,
      {
        headers: {
          ...this.getHeaders(),
          'Content-Language': locale,
        },
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async getDealDocuments(
    deal: Deal
  ): Promise<ApiResponse<DealDocument[]>> {
    const params = {
      fields: {
        deals_documents: ['category'].join(','),
        documents: ['name', 'created_at', 'user_name'].join(','),
        assets: ['normalized_address'].join(','),
        deal_stage: ['id', 'type'].join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<DealDocument[]>>(
      `v1/platform/deals/${deal.id}/documents`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<DealDocument[]>(response);
  }

  public async createDealDocument(
    deal_id: string,
    dealDocument: DealDocument
  ): Promise<ApiResponse<DealDocument>> {
    const body = {
      data: dealDocument,
    };

    const response = await this.request.post<ApiResponseFormat<DealDocument>>(
      `/v1/platform/deals/${deal_id}/documents/${dealDocument.relationships?.document?.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<DealDocument>(response);
  }

  public async getDealStages(): Promise<ApiResponse<DealStage[]>> {
    const params = {
      fields: {
        deal_stages: ['order', 'name'].join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<DealStage[]>>(
      '/v1/platform/deal_stages',
      params,
      {
        headers: await this.getHeaders(),
      }
    );

    return processApiResponse<DealStage[]>(response);
  }

  public async updateDealStage(
    dealStage: DealStage
  ): Promise<ApiResponse<DealStage>> {
    const body = {
      data: dealStage,
    };

    const response = await this.request.put<ApiResponseFormat<DealStage>>(
      `/v1/platform/deal_stages/${dealStage.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<DealStage>(response);
  }

  public async createDealStage(
    dealStage: DealStage
  ): Promise<ApiResponse<DealStage>> {
    const body = {
      data: dealStage,
    };

    const response = await this.request.post<ApiResponseFormat<DealStage>>(
      `/v1/platform/deal_stages`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<DealStage>(response);
  }

  public async deleteDealStage(
    dealStageId: string,
    destinyDealStageId: string
  ): Promise<ApiResponse<void>> {
    const params = {
      data: {
        id: destinyDealStageId,
        type: 'deal_stages',
      },
    };

    const response = await this.request.delete<ApiResponseFormat<void>>(
      `/v1/platform/deal_stages/${dealStageId}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async createTask(
    dealId: string,
    task: Task
  ): Promise<ApiResponse<Task>> {
    const body = {
      fields: {
        tasks: taskAttributes.join(','),
        deals: ['id', 'type'].join(','),
        user_assignee: ['id', 'type'].join(','),
        deal_stage: ['id', 'type'].join(','),
      },
      data: task,
    };

    const response = await this.request.post<ApiResponseFormat<Task>>(
      `/v1/platform/deals/${dealId}/tasks`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Task>(response);
  }

  public async updateTask(
    dealId: string,
    task: Task
  ): Promise<ApiResponse<Task>> {
    const body = {
      fields: {
        tasks: taskAttributes.join(','),
        deals: ['id', 'type'].join(','),
        user_assignee: ['id', 'type'].join(','),
      },
      data: task,
    };

    const response = await this.request.put<ApiResponseFormat<Task>>(
      `/v1/platform/deals/${dealId}/tasks/${task.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Task>(response);
  }

  public async getTasks(
    deal: Deal,
    sort?: string
  ): Promise<ApiResponse<Task[]>> {
    const params = {
      fields: {
        tasks: taskAttributes.join(','),
        deals: 'id',
        user_assignee: 'id',
        deal_stage: 'id',
      },
      sort,
    };

    const response = await this.request.get<ApiResponseFormat<Task[]>>(
      `v1/platform/deals/${deal.id}/tasks`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Task[]>(response);
  }

  public async deleteTask(
    dealId: string,
    taskId: string
  ): Promise<ApiResponse<void>> {
    const response = await this.request.delete<ApiResponseFormat<void>>(
      `/v1/platform/deals/${dealId}/tasks/${taskId}`,
      {},
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async getFinancialModels(
    page: PaginationFilters = { number: 1, size: 9999 }
  ): Promise<ApiResponse<FinancialModel[]>> {
    const params = {
      fields: {
        financial_models: financialModelAttributes.join(','),
        user_owner: 'id',
        user_last_updater: 'id',
        assets: 'id',
        financial_datas: 'financial_account',
      },
      page,
    };

    const response = await this.request.get<
      ApiResponseFormat<FinancialModel[]>
    >('v1/platform/financials/models', params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<FinancialModel[]>(response);
  }

  public async getFinancialAccounts(): Promise<
    ApiResponse<FinancialAccount[]>
  > {
    const params = {
      fields: {
        financial_accounts: financialAccountAttributes.join(','),
        parent: 'id',
      },
    };

    const response = await this.request.get<
      ApiResponseFormat<FinancialAccount[]>
    >('v1/platform/financials/accounts', params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<FinancialAccount[]>(response);
  }

  public async getLoans(
    globalFilters: GlobalFilter[],
    filters?: LoansFilters,
    sort?: string,
    page?: PaginationFilters
  ): Promise<ApiResponse<Loan[]>> {
    const apiFilters = {
      ...filters,
      like: filters?.like,
      global: globalFilters,
    };

    const fields = {
      loans: loanAttributes.join(','),
      assets: ['id'].join(','),
      assets_loans: ['id', 'allocation'].join(','),
    };
    const params = {
      fields,
      page: page || {
        size: 99999,
      },
      sort: sort || '-created_at',
      filter: apiFilters,
    };

    const response = await this.request.get<ApiResponseFormat<Loan[]>>(
      '/v1/platform/loans',
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Loan[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async getLoan(loanId: string): Promise<ApiResponse<Loan>> {
    const params = {
      fields: {
        loans: loanAttributes.join(','),
        assets: ['id'].join(','),
        assets_loans: ['id', 'allocation'].join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<Loan>>(
      `/v1/platform/loans/${loanId}`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Loan>(response);
  }

  public async createLoan(loan: Loan): Promise<ApiResponse<Loan>> {
    const body = {
      data: loan,
    };

    const response = await this.request.post<ApiResponseFormat<Loan>>(
      '/v1/platform/loans',
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Loan>(response);
  }

  public async updateLoan(loan: Loan): Promise<ApiResponse<Loan>> {
    const body = {
      data: loan,
      disableClearEmptyValues: true,
    };
    const response = await this.request.put<ApiResponseFormat<Loan>>(
      `/v1/platform/loans/${loan.id}`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<Loan>(response);
  }

  public async deleteLoan(loan: Loan): Promise<ApiResponse<void>> {
    const response = await this.request.delete<ApiResponseFormat<void>>(
      `/v1/platform/loans/${loan.id}`,
      {},
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async exportLoans(
    globalFilters: GlobalFilter[],
    locale: Language
  ): Promise<ApiResponse<void>> {
    const body = {
      filter: {
        global: globalFilters,
      },
    };

    const response = await this.request.post<ApiResponseFormat<void>>(
      '/v1/platform/exports/loans',
      body,
      {
        headers: {
          ...this.getHeaders(),
          'Content-Language': locale,
        },
      }
    );

    return processApiResponse<void>(response, emptyProcessResponseResolver);
  }

  public async getPlatformTenant(
    tenantId: string
  ): Promise<ApiResponse<PlatformTenant>> {
    const response = await this.request.get<ApiResponseFormat<PlatformTenant>>(
      `/v1/platform/tenants/${tenantId}`,
      {
        fields: {
          tenants: tenantAttributes.join(','),
        },
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<PlatformTenant>(response);
  }

  public async getPlatformTenantsAssociated(
    filters?: PlatformTenantFilters,
    sort?: string,
    page?: PaginationFilters
  ): Promise<ApiResponse<PlatformTenant[]>> {
    const params = {
      fields: {
        tenants: tenantAttributes.join(','),
      },
      filter: filters,
      page,
      sort: sort || '-created_at',
    };

    const response = await this.request.get<
      ApiResponseFormat<PlatformTenant[]>
    >(`/v1/platform/tenants/associated`, params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<PlatformTenant[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async getPlatformTenantsAssociatedParents(
    filters?: PlatformTenantFilters,
    sort?: string,
    page?: PaginationFilters
  ): Promise<ApiResponse<PlatformTenant[]>> {
    const params = {
      fields: {
        tenants: tenantAttributes.join(','),
      },
      filter: filters,
      page,
      sort: sort || '-created_at',
    };

    const response = await this.request.get<
      ApiResponseFormat<PlatformTenant[]>
    >(`/v1/platform/tenants/associated_parents`, params, {
      headers: this.getHeaders(),
    });

    return processApiResponse<PlatformTenant[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async upsertTenant(
    ref_id: string,
    ref_type: string,
    name: string
  ): Promise<ApiResponse<PlatformTenant>> {
    const body = {
      data: { attributes: { ref_id, ref_type, name } },
      fields: { tenants: ['id'].join('') },
    };

    const response = await this.request.put<ApiResponseFormat<PlatformTenant>>(
      `/v1/platform/tenants`,
      body,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<PlatformTenant>(response);
  }

  public async createLoanStressReport(
    attributes: LoanStressReportAttributes
  ): Promise<ApiResponse<LoanStressReport>> {
    const response = await this.request.post<
      ApiResponseFormat<LoanStressReport>
    >(
      `/v1/platform/loan_stress_reports`,
      {
        data: {
          type: 'loan_stress_reports',
          attributes,
        },
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanStressReport>(response);
  }

  public async getLoanStressReports(
    sort?: string,
    page?: PaginationFilters
  ): Promise<ApiResponse<LoanStressReport[]>> {
    const response = await this.request.get<
      ApiResponseFormat<LoanStressReport[]>
    >(
      `/v1/platform/loan_stress_reports`,
      {
        fields: {
          loan_stress_reports: loanStressReportFields.join(','),
          users: ['email'].join(','),
        },
        page,
        sort: sort || '-created_at',
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanStressReport[]>(
      response,
      paginatedProcessResponseResolver
    );
  }

  public async getLoanStressReport(
    reportId: string
  ): Promise<ApiResponse<LoanStressReport>> {
    const response = await this.request.get<
      ApiResponseFormat<LoanStressReport>
    >(
      `/v1/platform/loan_stress_reports/${reportId}`,
      {
        fields: {
          loan_stress_reports: loanStressReportFields.join(','),
          users: 'email',
          input_documents: 'id',
          output_documents: 'id',
        },
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanStressReport>(response);
  }

  public async getFiltersAssets(
    filter: FiltersBarFilter, fields: string[]
  ): Promise<ApiResponse<FilterBar[]>> {
    const params = {
      filter,
      fields: {
        filters_options: fields.join(','),
      },
    };

    const response = await this.request.get<ApiResponseFormat<FilterBar[]>>(
      `/v1/platform/filters/assets`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<FilterBar[]>(response);
  }

  public async getFiltersTenants(
    filter: FiltersBarFilter
  ): Promise<ApiResponse<FilterBar[]>> {
    const params = {
      filter,
    };

    const response = await this.request.get<ApiResponseFormat<FilterBar[]>>(
      `/v1/platform/filters/associated_tenants`,
      params,
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<FilterBar[]>(response);
  }

  public async getLoanSensitivityAnalysis(
    analysisId: string
  ): Promise<ApiResponse<LoanSensitivityAnalysis>> {
    const response = await this.request.get<
      ApiResponseFormat<LoanSensitivityAnalysis>
    >(
      `/v1/platform/loan_sensitivity_analyses/${analysisId}`,
      {
        fields: {
          loan_sensitivity_analyses: loanSensitivityAnalysisFields.join(','),
          users: ['email'].join(','),
        },
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanSensitivityAnalysis>(response);
  }

  public async getLoanSensitivityAnalyses(
    loanId: string,
    sort?: string,
    page?: PaginationFilters
  ): Promise<ApiResponse<LoanSensitivityAnalysis[]>> {
    const response = await this.request.get<
      ApiResponseFormat<LoanSensitivityAnalysis[]>
    >(
      `/v1/platform/loan_sensitivity_analyses`,
      {
        filter: {
          loan_id: loanId,
        },
        fields: {
          loan_sensitivity_analyses: loanSensitivityAnalysisFields.join(','),
          users: ['id', 'type', 'email'].join(','),
        },
        page,
        sort: sort || '-created_at',
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanSensitivityAnalysis[]>(response);
  }

  public async createLoanSensitivityAnalysis(
    loanId: string,
    attributes: LoanSensitivityAnalysisAttributes
  ): Promise<ApiResponse<LoanSensitivityAnalysis>> {
    const response = await this.request.post<
      ApiResponseFormat<LoanSensitivityAnalysis>
    >(
      `/v1/platform/loan_sensitivity_analyses`,
      {
        data: {
          type: 'loan_sensitivity_analyses',
          attributes,
          relationships: {
            loan: {
              id: loanId,
              type: 'loans',
            },
          },
        },
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanSensitivityAnalysis>(response);
  }

  public async deleteLoanSensitivityAnalysis(
    analysisId: string
  ): Promise<ApiResponse<LoanSensitivityAnalysis>> {
    const response = await this.request.delete<
      ApiResponseFormat<LoanSensitivityAnalysis>
    >(
      `/v1/platform/loan_sensitivity_analyses/${analysisId}`,
      {},
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanSensitivityAnalysis>(response);
  }

  public async setLoanSensitivityAnalysis(
    analysisId: string,
    attributes: LoanSensitivityAnalysisAttributes
  ): Promise<ApiResponse<LoanSensitivityAnalysis>> {
    const response = await this.request.put<
      ApiResponseFormat<LoanSensitivityAnalysis>
    >(
      `/v1/platform/loan_sensitivity_analyses/${analysisId}`,
      {
        data: {
          type: 'loan_sensitivity_analyses',
          attributes,
        },
        disableClearEmptyValues: true,
      },
      {
        headers: this.getHeaders(),
      }
    );

    return processApiResponse<LoanSensitivityAnalysis>(response);
  }

  public async postLoanStressReportDocument(
    reportId: string,
    documentId: string,
    companyId: string,
    documentType: 'input' | 'output'
  ): Promise<ApiResponse<LoanStressReportDocumentResponse>> {
    const params: LoanStressReportDocument = {
      type: 'loan_stress_report_documents',
      attributes: {
        document_type: [documentType].join(','),
      },
      relationships: {
        company: {
          type: 'companies',
          id: companyId,
        },
      },
    };
    const response = await this.request.post<
      ApiResponseFormat<LoanStressReportDocumentResponse>
    >(
      `/v1/platform/loan_stress_reports/${reportId}/documents/${documentId}`,
      { data: params },
      { headers: this.getHeaders() }
    );

    return processApiResponse<LoanStressReportDocumentResponse>(response);
  }

  public async getGlobalFilters(filter?: GlobalFilterFilter): Promise<ApiResponse<GlobalFilter[]>> {
    const response = await this.request.get<
    ApiResponseFormat<GlobalFilter[]>
  >(
    `/v1/platform/assets/global_filters`,
    {
      filter,
    },
    {
      headers: this.getHeaders(),
    }
  );

  return processApiResponse<GlobalFilter[]>(response);
  }
}

export const platformApi = new PlatformApi();

export { PlatformApi };
